import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  FaBuilding, FaCalculator, FaLaptopCode, FaBullhorn, FaBriefcase, FaChartLine,
} from 'react-icons/fa';
import servicesBg from '../assets/images/services-bg.jpg';
import './Services.css';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const services = [
  {
    icon: <FaBuilding />,
    title: 'Business Registration',
    color: '#1a3a5c',
    items: [
      'Proprietorship Registration',
      'Partnership Firm',
      'LLP Registration',
      'Private Limited Company',
      'Udyam (MSME) Registration',
      'Shop Act License',
      'FSSAI Registration',
      'Trade License',
    ],
  },
  {
    icon: <FaCalculator />,
    title: 'Accounting & GST',
    color: '#1e4038',
    items: [
      'GST Registration',
      'GST Return Filing',
      'Income Tax Filing (ITR)',
      'TDS Filing',
      'Bookkeeping & Accounting',
      'Balance Sheet Preparation',
    ],
  },
  {
    icon: <FaLaptopCode />,
    title: 'Website Development',
    color: '#2d1e4f',
    items: [
      'Static Business Website (5 pages)',
      'Dynamic Business Website',
      'Landing Pages',
      'E-commerce Website',
      'Website Maintenance',
    ],
  },
  {
    icon: <FaBullhorn />,
    title: 'Digital Marketing',
    color: '#3d1e1e',
    items: [
      'Social Media Marketing',
      'Lead Generation Ads',
      'Google Ads',
      'Logo & Branding Design',
      'Content Creation',
      'SEO Services',
    ],
  },
  {
    icon: <FaBriefcase />,
    title: 'Business Management',
    color: '#1e3530',
    items: [
      'Business Process Setup',
      'SOP Creation',
      'Growth Consulting',
      'Business Strategy Planning',
    ],
  },
  {
    icon: <FaChartLine />,
    title: 'Financial Modelling',
    color: '#1e2d45',
    items: [
      'Business Projections & Forecasting',
      'Investment Planning',
      'Cash Flow Analysis',
      'Pitch Deck with Financials',
    ],
  },
];

function ServiceCard({ service, index }) {
  return (
    <motion.div
      className="service-card"
      variants={fadeUp}
      whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(0,0,0,0.12)' }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <div className="service-card__icon" style={{ background: service.color }}>
        {service.icon}
      </div>
      <h3 className="service-card__title">{service.title}</h3>
      <ul className="service-card__list">
        {service.items.map((item) => (
          <li key={item}>
            <span className="service-card__check">✓</span> {item}
          </li>
        ))}
      </ul>
      <a
        href="#contact"
        className="service-card__link"
        onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
      >
        Get Started →
      </a>
    </motion.div>
  );
}

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section className="section services" id="services" style={{ backgroundImage: `url(${servicesBg})` }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <p className="section-label">What We Offer</p>
          <h2 className="section-title">Our Services</h2>
          <div className="gold-divider" />
          <p className="section-subtitle">Everything your business needs under one roof</p>
        </motion.div>

        <motion.div
          ref={ref}
          className="services__grid"
          variants={stagger}
          initial="hidden"
          animate={inView ? 'show' : 'hidden'}
        >
          {services.map((s, i) => (
            <ServiceCard key={s.title} service={s} index={i} />
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="services__cta"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <a
            href="#contact"
            className="btn-primary"
            style={{ color: 'var(--navy)' }}
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
          >
            Discuss Your Requirement
          </a>
        </motion.div>
      </div>
    </section>
  );
}
